/**
 * Checkpoint-history sparkline for the game-over screen: the hidden rating at
 * the start of the run and after every checkpoint, drawn as an inline SVG path.
 * Pure geometry — the component only renders the strings.
 */
import { LEVEL_WIDTH, levelForRating, type RunSummary } from '@dropstack/core';

export interface SparkPoint {
  x: number;
  y: number;
}

export interface SparklineGeometry {
  /** Stroke path through every point ("M x y L x y …"). */
  line: string;
  /** Closed path from the line down to the baseline (for a soft fill). */
  area: string;
  points: SparkPoint[];
}

export interface SparklineBox {
  width: number;
  height: number;
  /** Inner margin so round line caps and end dots aren't clipped. */
  padding: number;
}

/** The value range drawn from bottom to top. */
export interface SparklineRange {
  min: number;
  max: number;
}

/** Rating at the start and after each checkpoint. */
export function ratingSeries(summary: Pick<RunSummary, 'startRating' | 'checkpoints'>): number[] {
  return [summary.startRating, ...summary.checkpoints.map((c) => c.ratingAfter)];
}

/** Level at the start and after each checkpoint (for the accessible description). */
export function levelSeries(summary: Pick<RunSummary, 'levelStart' | 'checkpoints'>): number[] {
  return [summary.levelStart, ...summary.checkpoints.map((c) => c.levelAfter)];
}

/**
 * A range around the values that spans at least `minSpan` (default one level
 * band), so a small wobble inside a level doesn't look like a cliff.
 */
export function sparklineRange(values: readonly number[], minSpan = LEVEL_WIDTH): SparklineRange {
  if (values.length === 0) return { min: 0, max: minSpan };
  let min = Math.min(...values);
  let max = Math.max(...values);
  const missing = minSpan - (max - min);
  if (missing > 0) {
    min -= missing / 2;
    max += missing / 2;
  }
  return { min, max };
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Path geometry for `values` in `box`. Higher values sit higher (smaller y).
 * One value draws a flat line across; no values → null.
 */
export function buildSparkline(
  values: readonly number[],
  box: SparklineBox,
  range: SparklineRange = sparklineRange(values),
): SparklineGeometry | null {
  if (values.length === 0) return null;
  const series = values.length === 1 ? [values[0]!, values[0]!] : values;
  const innerW = Math.max(0, box.width - 2 * box.padding);
  const innerH = Math.max(0, box.height - 2 * box.padding);
  const span = range.max - range.min || 1;
  const points = series.map((v, i) => ({
    x: round2(box.padding + (innerW * i) / (series.length - 1)),
    y: round2(box.padding + innerH * (1 - (Math.min(range.max, Math.max(range.min, v)) - range.min) / span)),
  }));
  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ');
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const bottom = round2(box.height - box.padding);
  const area = `${line} L${last.x} ${bottom} L${first.x} ${bottom} Z`;
  return { line, area, points: values.length === 1 ? [first] : points };
}

/** Level marker lines (band boundaries) that fall inside the range, as y positions. */
export function levelGridLines(range: SparklineRange, box: SparklineBox): { level: number; y: number }[] {
  const span = range.max - range.min || 1;
  const innerH = Math.max(0, box.height - 2 * box.padding);
  const out: { level: number; y: number }[] = [];
  const first = Math.ceil(range.min / LEVEL_WIDTH);
  for (let k = first; k * LEVEL_WIDTH <= range.max; k++) {
    const rating = k * LEVEL_WIDTH;
    if (rating <= range.min) continue;
    out.push({ level: levelForRating(rating), y: round2(box.padding + innerH * (1 - (rating - range.min) / span)) });
  }
  return out;
}
